import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Building2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import LanguageToggle from "../components/LanguageToggle";
import Logo from "../components/Logo";
import { useT } from "../lib/i18n";

export default function Register() {
  const { register } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const payload = {
      ...form,
      email: form.email.trim().toLowerCase(),
      role: "sponsor",
    };
    const r = await register(payload);
    setBusy(false);
    if (r.ok) {
      toast.success("Broker account created");
      nav("/broker", { replace: true });
    } else {
      setErr(r.error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="mb-4 flex justify-end"><LanguageToggle /></div>
        <Link to="/" className="mb-6 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-slate-200" data-testid="back-home-link">
          <Logo height={120} />
        </Link>
        <Card className="glass-card border-0 p-7">
          <div className="mb-5 flex items-start gap-3 rounded-md border border-emerald-500/25 bg-emerald-500/[0.06] p-4" data-testid="register-open-notice">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
            <div>
              <div className="text-sm font-semibold text-emerald-200">Free broker account</div>
              <div className="mt-1 text-xs text-emerald-100/80">
                Create your broker account in seconds — it's free.
              </div>
            </div>
          </div>
          <div className="mb-6">
            <h1 className="font-display text-3xl font-medium tracking-tight">{t("register.title")}</h1>
            <p className="mt-1 text-sm text-slate-400">
              {t("register.body")}
            </p>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="register-form">
            <div className="sm:col-span-2">
              <Label htmlFor="name" className="text-slate-300">Full Name</Label>
              <Input id="name" required value={form.name} onChange={update("name")} className="mt-1 border-slate-800 bg-slate-950 text-slate-50" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} className="mt-1 border-slate-800 bg-slate-950 text-slate-50" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-slate-300">Phone</Label>
              <Input id="phone" value={form.phone} onChange={update("phone")} className="mt-1 border-slate-800 bg-slate-950 text-slate-50" data-testid="register-phone-input" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="company" className="text-slate-300">Company <span className="text-slate-500">(optional)</span></Label>
              <Input id="company" value={form.company} onChange={update("company")} className="mt-1 border-slate-800 bg-slate-950 text-slate-50" data-testid="register-company-input" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="password" className="text-slate-300">{t("common.password")}</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={update("password")} className="mt-1 border-slate-800 bg-slate-950 text-slate-50" data-testid="register-password-input" />
            </div>
            {err && (
              <div className="sm:col-span-2 rounded-md border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="register-error">
                {err}
              </div>
            )}
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy} className="w-full bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60" data-testid="register-submit-btn">
                {busy ? "Creating account…" : "Create Broker Account"}
              </Button>
            </div>
          </form>
          <div className="mt-6 text-center text-sm text-slate-400">
            {t("register.already")}{" "}
            <Link to="/login" className="text-emerald-300 hover:text-emerald-200" data-testid="go-login-link">{t("common.signIn")}</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
